import { Button, ConfigProvider, Header, Link, Role, useAuthorization, useBoolean, useConfig, User, useToken } from 'common';
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const onHoverAnimation = keyframes`
 0% { width: 0; opacity: 0; }
 50% { opacity: 0.5; }
 100% { width: 100%; opacity: 1 }
`;

const Menu = styled.nav<{ isShowMobileMenu: boolean }>`
  background-color: #2362a2;
  display: block;
  height: 64px;
  bottom: 0;
  padding-bottom: 1.25rem;

  ul {
    display: flex;
    text-align: center;
    justify-content: center;
    column-gap: 4em;
    margin-top: 0;
    margin-bottom: 0;
    padding-inline-start: 0;
    padding: 0 10px;
  }

  li {
    list-style: none;
    display: inline-block;
    text-transform: uppercase;
  }
  a {
    padding: 20px 0;
    cursor: pointer;
  }
  a:not(.selected):hover {
    position: relative;
    color: #ebebeb;
    text-decoration: ${(props) => props.isShowMobileMenu && 'underline'};
    transition: 0.3s;
    &:hover::before {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      right: 0;
      border-top: ${(props) => !props.isShowMobileMenu && '2px solid #ebebeb'};
      top: 0;
      animation-name: ${onHoverAnimation};
      animation-duration: 0.2s;
    }
  }
  a.selected::before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    border-top: ${(props) => !props.isShowMobileMenu && '2px solid #ebebeb'};
    top: 0;
  }
  a.selected:hover {
    color: ${(props) => (props.isShowMobileMenu ? '#ebebeb' : '#ffffff')};
    text-decoration: ${(props) => props.isShowMobileMenu && 'underline'};
  }
`;

const Overlay = styled.div`
  z-index: 101;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(2px);
  background-color: rgba(0, 0, 0, 0.5);
`;

const MainMenuLink = styled(Link)`
  display: flex;
  height: 100%;
  font-style: normal;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-decoration: none;
  color: #ffffff;
  transform: scale(1, 1);
  transition: 0.2s ease-out;
`;

const MobileMenu = styled(Menu)`
  height: 100vh;
  position: absolute;
  z-index: 101;
  top: 0;
  right: 0;
  width: 22rem;
  padding-top: 7.5rem;
  ul {
    flex-direction: column;
    background-color: #2362a2;
    margin-top: 0;
    margin-bottom: 0;
    padding-inline-start: 0;
    padding: 0;
    li:last-child {
      margin-bottom: 25px;
    }
    li:last-child::after {
      content: '';
      display: block;
      position: absolute;
      margin: 15px 0 15px -15px;
      width: 100%;
      border-bottom: 1px solid #3077b7;
    }
    a {
      padding: 0;
    }
  }
  li {
    margin: 0;
    padding: 15px 48px 15px 36px;
    @media only screen and (max-width: 325px) {
      text-align: initial;
    }
  }
  @media only screen and (max-width: 700px) {
    width: 70%;
  }
  @media only screen and (max-width: 450px) {
    width: 80%;
  }
  @media only screen and (max-width: 380px) {
    width: 100%;
  }
`;

const StyledLoginButton = styled(Button)`
  height: 40px;
  display: flex;
  align-items: center;
  padding: 14px 30px 10px 30px;
  margin-left: 20px;
  font-style: normal;
  transition: 0.2s;
  &:hover {
    color: #2362a2;
  }
`;

const StyledLogo = styled.img`
  position: absolute;
  top: 0;
  margin: 11px 0 0 20px;
`;

export const isInRole = (obsahuje: any, role: any) => {
  let constains = false;
  obsahuje.forEach((roli: any) => {
    if (role?.includes(roli)) {
      constains = true;
    }
  });
  return constains;
};

type IProps = {
  jumpToContent: () => void;
  mainMenuRef: React.RefObject<HTMLDivElement>;
};

const MainMenu = ({ jumpToContent, mainMenuRef }: IProps): JSX.Element => {
  const token = useToken();
  const { roleIds, roleNames, name, surname, mandat, entityName, entityType, entityId } = token?.getDataFromToken() || {};
  const [role, setRole] = useState<string>();
  const { disableAuth, posudkyUrl, ukonyUrl, zadostiUrl, zasobnikUrl } = useConfig();
  const isMobileMode = useBoolean(false);
  const [isShowMobileMenu, setIsShowMobileMenu] = useState(false);
  const [screenSize, getDimension] = useState({
    dynamicWidth: window.innerWidth,
    dynamicHeight: window.innerHeight,
  });
  const auth = useAuthorization();

  const setDimension = () => {
    getDimension({
      dynamicWidth: window.innerWidth,
      dynamicHeight: window.innerHeight,
    });
  };

  useEffect(() => {
    window.addEventListener('resize', setDimension);
    if (screenSize.dynamicWidth < 1101) {
      isMobileMode.setTrue();
      setIsShowMobileMenu(false);
    } else {
      isMobileMode.setFalse();
      setIsShowMobileMenu(false);
    }

    return () => {
      window.removeEventListener('resize', setDimension);
    };
  }, [screenSize]);

  if (token.getDataFromToken() && role !== token.getDataFromToken().roleIds) {
    setRole(token.getDataFromToken().roleIds);
  }

  const getLink = () => {
    const data = token.getDataFromToken();
    return `/${data.entityType}/detail/${data.entityId}`;
  };

  const onClickHandle = () => {
    setIsShowMobileMenu(!isShowMobileMenu);
    document.body.style.position = !isShowMobileMenu ? 'fixed' : 'unset';
  };

  const login = async () => {
    token.redirectLogin();
    setIsShowMobileMenu(false);
  };

  const logout = async () => {
    token.logout();
    setIsShowMobileMenu(false);
  };

  return (
    <>
      <ConfigProvider typ="znalec">
        <div ref={mainMenuRef}>
          <Header jumpToContent={jumpToContent} isPubWeb={false} isMobileMode={isMobileMode.value} isShowMobileMenu={isShowMobileMenu} setIsShowMobileMenu={setIsShowMobileMenu} />
        </div>
      </ConfigProvider>
      {!isMobileMode.value ? (
        <Menu role="navigation" id="nav" isShowMobileMenu={isShowMobileMenu}>
          <ul>
            {(disableAuth || isInRole([Role.Editor], role)) && (
              <li>
                <MainMenuLink exact={true} to="/" activeClassName="selected">
                  Přidat
                </MainMenuLink>
              </li>
            )}
            <li>
              <MainMenuLink to="/vyhledavani" activeClassName="selected">
                Vyhledávání v seznamech
              </MainMenuLink>
            </li>
            {(disableAuth || isInRole([Role.znalec], role)) && !posudkyUrl?.endsWith('undefined') && (
              <li>
                <MainMenuLink exact={true} to="/evidence-posudku" activeClassName="selected">
                  Přidat
                </MainMenuLink>
              </li>
            )}
            {(disableAuth || isInRole([Role.tlumocnik], role)) && !ukonyUrl?.endsWith('undefined') && (
              <li>
                <MainMenuLink exact={true} to="/evidence-ukonu" activeClassName="selected">
                  Přidat
                </MainMenuLink>
              </li>
            )}
            {(disableAuth || isInRole([Role.Spravce, Role.Editor], role)) && (
              <>
                {!zasobnikUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/zasobnik-prace" activeClassName="selected">
                      Zásobník práce
                    </MainMenuLink>
                  </li>
                )}
                {!posudkyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/evidence-posudku" activeClassName="selected">
                      Evidence posudků
                    </MainMenuLink>
                  </li>
                )}
                {!ukonyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/evidence-ukonu" activeClassName="selected">
                      Evidence úkonů
                    </MainMenuLink>
                  </li>
                )}
                <li>
                  <MainMenuLink to="/nastaveni" activeClassName="selected">
                    Nastavení
                  </MainMenuLink>
                </li>
              </>
            )}
            {isInRole([Role.nahlizejici], role) && (
              <>
                {!posudkyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/evidence-posudku" activeClassName="selected">
                      Evidence posudků
                    </MainMenuLink>
                  </li>
                )}
                {!ukonyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/evidence-ukonu" activeClassName="selected">
                      Evidence úkonů
                    </MainMenuLink>
                  </li>
                )}
              </>
            )}
            {isInRole([Role.znalec, Role.tlumocnik], role) && (
              <li>
                <MainMenuLink to={getLink} activeClassName="selected">
                  Můj profil
                </MainMenuLink>
              </li>
            )}
            {isInRole([Role.znalec], role) && !posudkyUrl?.endsWith('undefined') && (
              <li>
                <MainMenuLink exact={true} to="/evidence-posudku/vysledky" activeClassName="selected">
                  Moje posudky
                </MainMenuLink>
              </li>
            )}
            {isInRole([Role.tlumocnik], role) && !ukonyUrl?.endsWith('undefined') && (
              <li>
                <MainMenuLink exact={true} to="/evidence-ukonu/vysledky" activeClassName="selected">
                  Moje úkony
                </MainMenuLink>
              </li>
            )}
            {(disableAuth || isInRole([Role.nahlizejicinazadost], role)) && !zadostiUrl?.endsWith('undefined') && (
              <li>
                <MainMenuLink to="/elektronicka-zadost" activeClassName="selected">
                  Žádosti o přístup k údajům
                </MainMenuLink>
              </li>
            )}
          </ul>
        </Menu>
      ) : (
        isShowMobileMenu && (
          <Overlay>
            <MobileMenu role="navigation" id="nav" isShowMobileMenu={isShowMobileMenu}>
              {isMobileMode && <StyledLogo src="/logo.svg" alt="logo" />}
              <ul>
                {(disableAuth || isInRole([Role.Editor], role)) && (
                  <li>
                    <MainMenuLink exact={true} to="/" activeClassName="selected" onClick={onClickHandle}>
                      Přidat
                    </MainMenuLink>
                  </li>
                )}
                <li>
                  <MainMenuLink to="/vyhledavani" activeClassName="selected" onClick={onClickHandle}>
                    Vyhledávání v seznamech
                  </MainMenuLink>
                </li>
                {(disableAuth || isInRole([Role.znalec], role)) && !posudkyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink exact={true} to="/evidence-posudku" activeClassName="selected" onClick={onClickHandle}>
                      Přidat
                    </MainMenuLink>
                  </li>
                )}
                {(disableAuth || isInRole([Role.tlumocnik], role)) && !ukonyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink exact={true} to="/evidence-ukonu" activeClassName="selected" onClick={onClickHandle}>
                      Přidat
                    </MainMenuLink>
                  </li>
                )}
                {(disableAuth || isInRole([Role.Spravce, Role.Editor], role)) && (
                  <>
                    {!zasobnikUrl?.endsWith('undefined') && (
                      <li>
                        <MainMenuLink to="/zasobnik-prace" activeClassName="selected" onClick={onClickHandle}>
                          Zásobník práce
                        </MainMenuLink>
                      </li>
                    )}
                    {!posudkyUrl?.endsWith('undefined') && (
                      <li>
                        <MainMenuLink to="/evidence-posudku" activeClassName="selected" onClick={onClickHandle}>
                          Evidence posudků
                        </MainMenuLink>
                      </li>
                    )}
                    {!ukonyUrl?.endsWith('undefined') && (
                      <li>
                        <MainMenuLink to="/evidence-ukonu" activeClassName="selected" onClick={onClickHandle}>
                          Evidence úkonů
                        </MainMenuLink>
                      </li>
                    )}
                    <li>
                      <MainMenuLink to="/nastaveni" activeClassName="selected" onClick={onClickHandle}>
                        Nastavení
                      </MainMenuLink>
                    </li>
                  </>
                )}
                {isInRole([Role.nahlizejici], role) && (
                  <>
                    {!posudkyUrl?.endsWith('undefined') && (
                      <li>
                        <MainMenuLink to="/evidence-posudku" activeClassName="selected" onClick={onClickHandle}>
                          Evidence posudků
                        </MainMenuLink>
                      </li>
                    )}
                    {!ukonyUrl?.endsWith('undefined') && (
                      <li>
                        <MainMenuLink to="/evidence-ukonu" activeClassName="selected" onClick={onClickHandle}>
                          Evidence úkonů
                        </MainMenuLink>
                      </li>
                    )}
                  </>
                )}
                {isInRole([Role.znalec, Role.tlumocnik], role) && (
                  <li>
                    <MainMenuLink to={getLink} onClick={onClickHandle} activeClassName="selected">
                      Můj profil
                    </MainMenuLink>
                  </li>
                )}
                {isInRole([Role.znalec], role) && !posudkyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink exact={true} to="/evidence-posudku/vysledky" activeClassName="selected" onClick={onClickHandle}>
                      Moje posudky
                    </MainMenuLink>
                  </li>
                )}
                {isInRole([Role.tlumocnik], role) && !ukonyUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink exact={true} to="/evidence-ukonu/vysledky" activeClassName="selected" onClick={onClickHandle}>
                      Moje úkony
                    </MainMenuLink>
                  </li>
                )}
                {(disableAuth || isInRole([Role.nahlizejicinazadost], role)) && !zadostiUrl?.endsWith('undefined') && (
                  <li>
                    <MainMenuLink to="/elektronicka-zadost" activeClassName="selected" onClick={onClickHandle}>
                      Žádosti o přístup k údajům
                    </MainMenuLink>
                  </li>
                )}
              </ul>
              {token.value && (
                <User
                  name={`${name} ${surname}`}
                  entityName={entityName}
                  userMandat={mandat}
                  onLogoutClick={logout}
                  entityType={entityType}
                  entityId={entityId}
                  isMobileMode={isMobileMode.value}
                  roleNames={isInRole([Role.Editor, Role.Spravce, Role.nahlizejici], roleIds) ? roleNames : undefined}
                />
              )}
              {!token.value && (
                <StyledLoginButton type="button" primary={false} onClick={auth?.isAccessDenied.value ? logout : login} name={auth?.isAccessDenied.value ? 'Odhlasit' : 'Prihlasit'}>
                  {auth?.isAccessDenied.value ? 'Odhlásit' : 'Přihlásit'}
                </StyledLoginButton>
              )}
            </MobileMenu>
          </Overlay>
        )
      )}
    </>
  );
};

export default MainMenu;
